import React, { useMemo, useState } from 'react';
import axios from 'axios';

export default function Radiology() {
  const [folderFiles, setFolderFiles] = useState([]);
  const [singleFiles, setSingleFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  const videoRef = React.useRef(null);
  const htmlRef = React.useRef(null);
  const pdfRef = React.useRef(null);

  const files = useMemo(() => [...folderFiles, ...singleFiles], [folderFiles, singleFiles]);

  function onFiles(e) {
    const list = Array.from(e.target.files || []);
    setSingleFiles(list);
  }
  function onFolder(e) {
    const list = Array.from(e.target.files || []);
    setFolderFiles(list);
  }
  function onDrop(ev) {
    ev.preventDefault();
    setDrag(false);
    const list = Array.from(ev.dataTransfer?.files || []);
    if (list.length) setFolderFiles(list);
  }
  function onDragOver(ev) { ev.preventDefault(); setDrag(true); }
  function onDragLeave() { setDrag(false); }

  async function onSubmit() {
    if (!files.length) return;
    setLoading(true); setError(''); setResult(null); setShowProgress(true); setProgress(0);
    let processingInterval;
    try {
      const fd = new FormData();
      files.forEach((f) => {
        const rel = (f.webkitRelativePath && f.webkitRelativePath.length) ? f.webkitRelativePath : f.name;
        fd.append('files', f, rel);
      });
      const resPromise = axios.post(`${process.env.REACT_APP_API_URL}/api/radiology/analyze`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxBodyLength: Infinity,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.min(70, Math.round((evt.loaded / evt.total) * 70));
          setProgress((p) => (pct > p ? pct : p));
        },
      });
      processingInterval = setInterval(() => setProgress((p) => (p < 95 ? p + 1 : p)), 400);
      const res = await resPromise;
      setResult(res.data);
      setProgress(100);
    } catch (err) {
      setError(err.response?.data?.detail?.error || err.message);
    } finally {
      if (processingInterval) clearInterval(processingInterval);
      setLoading(false);
      setTimeout(() => setShowProgress(false), 1200);
    }
  }

  function clearFolderSelection() { setFolderFiles([]); }
  function clearFilesSelection() { setSingleFiles([]); }

  return (
    <div className="page">
      <header className="header"><h1>Radiology</h1></header>
      <main className="container">
        <section className="panel rad-hero">
          <h2 className="title-center">AI CT Analysis: Lung Nodule Detection</h2>
          <p className="lead-center">Upload a DICOM CT series and our pipeline segments lungs, detects nodules, runs 3D volumetry and generates a PDF report, annotated video, and an interactive 3D HTML view.</p>
        </section>

        <section className="panel rad-flow">
          <h3 className="flow-title">How It Works</h3>
          <div className="flow-grid">
            <div className="flow-step"><div className="ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7h6l2 2h10v10H3z"/></svg></div><div className="step-title">1) Ingest</div><p>Folder-safe DICOM intake with path normalization and series discovery.</p></div>
            <div className="flow-step"><div className="ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="12" r="3"/><circle cx="15" cy="12" r="3"/></svg></div><div className="step-title">2) Segment</div><p>Lung segmentation to constrain detection and suppress noise.</p></div>
            <div className="flow-step"><div className="ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="6"/><path d="M20 20l-3.5-3.5"/></svg></div><div className="step-title">3) Detect</div><p>Deep model detects candidate nodules; filters artifacts and merges slices.</p></div>
            <div className="flow-step"><div className="ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="8" width="18" height="6" rx="2"/><path d="M7 8v6M11 8v6M15 8v6"/></svg></div><div className="step-title">4) Quantify</div><p>3D volumetry and diameter estimates; composition notes when available.</p></div>
            <div className="flow-step"><div className="ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 3h9l3 3v15H6z"/><path d="M15 3v3h3"/></svg></div><div className="step-title">5) Report</div><p>Generate PDF summaries and an annotated review video.</p></div>
            <div className="flow-step"><div className="ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/></svg></div><div className="step-title">6) 3D View</div><p>Interactive HTML lung view for spatial context and review.</p></div>
          </div>
          <div className="notes-band">
            <div className="notes-title">Key Advantages</div>
            <div className="notes-grid">
              <div className="note-item"><span className="chip" /> Fast outputs (PDF/MP4/HTML)</div>
              <div className="note-item"><span className="chip" /> Robust segmentation + detection</div>
              <div className="note-item"><span className="chip" /> Drop-in backend with safe file handling</div>
            </div>
          </div>
        </section>

        <div className="panel">
          <h2 style={{marginTop:0}}>Run Analysis</h2>

          <div className="upload-row" style={{marginTop:12}}>
            <div className="upload-card">
              <div className="upload-title">Choose Folder</div>
              <div className={`dropzone ${drag ? 'drag' : ''}`} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
                <div className="dz-inner">
                  <div className="dz-ico"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M10 4h8a2 2 0 0 1 2 2v2H4V6a2 2 0 0 1 2-2h4z"/><path d="M4 9h16v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/></svg></div>
                  <div className="dz-text">Select a DICOM folder to upload or drag and drop here</div>
                  <label className="dz-btn">
                    <input type="file" multiple webkitdirectory="true" directory="true" onChange={onFolder} style={{display:'none'}} />
                    Choose Folder
                  </label>
                </div>
              </div>
              {folderFiles.length > 0 && (
                <div className="sel-card">
                  <div className="sel-left">
                    <span className="sel-ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M10 4h8a2 2 0 0 1 2 2v2H4V6a2 2 0 0 1 2-2h4z"/><path d="M4 9h16v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/></svg></span>
                    <div>
                      <div className="sel-title">Folder Selection</div>
                      <div className="sel-sub">{folderFiles.length} files selected</div>
                    </div>
                  </div>
                  <button className="sel-x" onClick={clearFolderSelection} aria-label="Clear folder selection">×</button>
                </div>
              )}
            </div>
            <div className="upload-card">
              <div className="upload-title">Choose Files</div>
              <div className="dropzone">
                <div className="dz-inner">
                  <div className="dz-ico"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M6 2h9l3 3v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v3h3"/></svg></div>
                  <div className="dz-text">Select .dcm files to upload</div>
                  <label className="dz-btn">
                    <input type="file" accept=".dcm" multiple onChange={onFiles} style={{display:'none'}} />
                    Choose Files
                  </label>
                </div>
              </div>
              {singleFiles.length > 0 && (
                <div className="sel-card">
                  <div className="sel-left">
                    <span className="sel-ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M6 2h9l3 3v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v3h3"/></svg></span>
                    <div>
                      <div className="sel-title">Files Selection</div>
                      <div className="sel-sub">{singleFiles.length} files selected</div>
                    </div>
                  </div>
                  <button className="sel-x" onClick={clearFilesSelection} aria-label="Clear files selection">×</button>
                </div>
              )}
            </div>
          </div>
          <div className="button-row rad-actions">
            <button className="btn-wide" onClick={onSubmit} disabled={loading || files.length === 0}>{loading ? 'Analyzing...' : 'Run Analysis'}</button>
          </div>

          {showProgress && (
            <div className="progress-card" aria-live="polite">
              <div className="p-top">
                <div className="ring" style={{background: `conic-gradient(#2c47ff ${progress*3.6}deg, #e8edff 0deg)`}}>
                  <div className="ring-inner" />
                </div>
                <div className="pct"><strong>{progress}%</strong>&nbsp; It will be completed soon...</div>
              </div>
              <div className="bar"><span style={{width: `${progress}%`}} /></div>
            </div>
          )}

          {error && <p className="status error" style={{marginTop:12}}>{error}</p>}

          {result && (
            <div style={{marginTop:16}}>
              {result.files?.video && (
                <div className="chart-card" style={{padding:0, overflow:'hidden'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', borderBottom:'1px solid #e6eaf1'}}>
                    <h3 style={{margin:0}}>Annotated Video</h3>
                    <button onClick={() => { const el = videoRef.current?.parentElement; if (el?.requestFullscreen) el.requestFullscreen(); }}>Fullscreen</button>
                  </div>
                  <div style={{background:'#000'}} ref={videoRef}>
                    <video controls preload="metadata" style={{width:'100%', maxHeight:'70vh', display:'block', background:'#000'}}>
                      <source src={`${process.env.REACT_APP_API_URL}/api/radiology/download?job_id=${result.job_id}&file=${encodeURIComponent(result.files.video)}`} type={result.files.video.toLowerCase().endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              )}

              {result.files?.html && (
                <div className="chart-card" style={{marginTop:16, padding:0, overflow:'hidden'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', borderBottom:'1px solid #e6eaf1'}}>
                    <h3 style={{margin:0}}>Interactive 3D View</h3>
                    <button onClick={() => { const el = htmlRef.current; if (el?.requestFullscreen) el.requestFullscreen(); }}>Fullscreen</button>
                  </div>
                  <iframe
                    ref={htmlRef}
                    title="3D Lung View"
                    src={`${process.env.REACT_APP_API_URL}/api/radiology/download?job_id=${result.job_id}&file=${encodeURIComponent(result.files.html)}`}
                    style={{width:'100%', height:'70vh', border:0}}
                    allowFullScreen
                  />
                </div>
              )}

              {result.files?.pdf && (
                <div className="chart-card" style={{marginTop:16, padding:0, overflow:'hidden'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', borderBottom:'1px solid #e6eaf1'}}>
                    <h3 style={{margin:0}}>PDF Report</h3>
                    <div>
                      <a className="quick" href={`${process.env.REACT_APP_API_URL}/api/radiology/download?job_id=${result.job_id}&file=${encodeURIComponent(result.files.pdf)}`} target="_blank" rel="noreferrer" style={{marginRight:8}}>Open in new tab</a>
                      <button onClick={() => { const el = pdfRef.current; if (el?.requestFullscreen) el.requestFullscreen(); }}>Fullscreen</button>
                    </div>
                  </div>
                  <iframe
                    ref={pdfRef}
                    title="PDF Report"
                    src={`${process.env.REACT_APP_API_URL}/api/radiology/download?job_id=${result.job_id}&file=${encodeURIComponent(result.files.pdf)}`}
                    style={{width:'100%', height:'85vh', border:0}}
                  />
                </div>
              )}

              <h3 style={{marginTop:18}}>Summary</h3>
              {(!result.summary || result.summary.length===0) && <p>No nodules detected.</p>}
              {result.summary && result.summary.length>0 && (
                <div className="chart-card">
                  <table style={{width:'100%', borderCollapse:'collapse'}}>
                    <thead>
                      <tr>
                        <th style={{textAlign:'left', padding:'6px'}}>#</th>
                        <th style={{textAlign:'left', padding:'6px'}}>Slice Range</th>
                        <th style={{textAlign:'left', padding:'6px'}}>Composition</th>
                        <th style={{textAlign:'left', padding:'6px'}}>Diameter (mm)</th>
                        <th style={{textAlign:'left', padding:'6px'}}>Volume (mm^3)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.summary.map((n, i) => (
                        <tr key={i}>
                          <td style={{padding:'6px'}}>{i+1}</td>
                          <td style={{padding:'6px'}}>{n.slice_range?.[0]} - {n.slice_range?.[1]}</td>
                          <td style={{padding:'6px'}}>{n.composition}</td>
                          <td style={{padding:'6px'}}>{n.diameter_mm?.toFixed ? n.diameter_mm.toFixed(2) : n.diameter_mm}</td>
                          <td style={{padding:'6px'}}>{n.volume_mm3?.toFixed ? n.volume_mm3.toFixed(2) : n.volume_mm3}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          )}
        </div>

        <style>{`
          .rad-hero { background: linear-gradient(180deg,#f7fbff,#ecf5ff); border:1px solid #e6eaf1; text-align:center; border-radius: 20px 20px 0 0; }
          .title-center { margin: 0 0 8px; font-size: 28px; font-weight: 800; }
          .lead-center { color:#2a3553; max-width: 900px; margin: 0 auto; }

          .rad-flow { margin-bottom: 36px; }
          .rad-flow .flow-title { margin:0 0 12px; font-size: 20px; }
          .flow-grid { display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; }
          .flow-step { background:#fff; border:1px solid #e6eaf1; border-radius:12px; padding:14px; box-shadow:0 8px 24px rgba(2,6,23,.06); }
          .flow-step .ico { width:36px; height:36px; display:flex; align-items:center; justify-content:center; background:#eef3ff; border:1px solid #d7e1ff; border-radius:10px; margin-bottom:8px; }
          .step-title { font-weight:700; margin-bottom:4px; }
          .notes-band { margin-top:12px; background:#eaf1ff; border:1px solid #cdd8ff; border-radius:14px; padding:16px; text-align:center; }
          .notes-title { color:#2C388F; font-weight:800; font-size:18px; }
          .notes-grid { margin-top:10px; display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; color:#2C388F; font-weight:600; }
          .note-item { display:flex; align-items:center; justify-content:center; gap:8px; }
          .note-item .chip { width:8px; height:8px; border-radius:999px; background:#2C388F; display:inline-block; }

          .upload-row { display:grid; grid-template-columns: 1fr 1fr; gap:12px; }
          .upload-card { background:#fff; border:1px solid #e6eaf1; border-radius:12px; padding:16px; box-shadow:0 8px 24px rgba(2,6,23,.06); }
          .upload-title { font-weight:700; color:#2a3553; margin-bottom:8px; }
          .dropzone { border:2px dashed #cdd8ff; background:#f5f9ff; border-radius:14px; padding:18px; text-align:center; transition: border-color .12s, background .12s; min-height: 180px; display:grid; place-items:center; }
          .dropzone.drag { border-color:#7aa2ff; background:#eef5ff; }
          .dz-inner { display:grid; place-items:center; gap:8px; color:#2C388F; }
          .dz-ico { width:44px; height:44px; display:flex; align-items:center; justify-content:center; background:#dfe8ff; border:1px solid #cdd8ff; border-radius:12px; }
          .dz-text { color:#2c388f; }
          .dz-btn { display:inline-block; margin-top:6px; background:#1e2a55; color:#fff; border:1px solid #1e2a55; border-radius:10px; padding:8px 12px; cursor:pointer; }
          .sel-card { margin-top:10px; background:#f5f7ff; border:1px solid #e6eaf1; border-radius:14px; padding:10px 12px; display:flex; align-items:center; justify-content:space-between; box-shadow:0 8px 18px rgba(2,6,23,.06); }
          .sel-left { display:flex; align-items:center; gap:10px; }
          .sel-ico { width:30px; height:30px; display:inline-flex; align-items:center; justify-content:center; background:#eaf1ff; border:1px solid #cdd8ff; border-radius:8px; }
          .sel-title { color:#1e2a55; font-weight:700; }
          .sel-sub { color:#6b7a99; font-size:12px; }
          .sel-x { background:transparent; border:none; color:#6b7a99; cursor:pointer; font-size:18px; line-height:1; }
          .sel-x:hover { color:#b02a37; }
          .rad-actions { display:flex; justify-content:center; margin-top: 24px; }
          .btn-wide { min-width: 260px; }

          .progress-card { margin-top:12px; background:#fff; border:1px solid #e6eaf1; border-radius:12px; padding:14px; box-shadow:0 8px 24px rgba(2,6,23,.06); }
          .p-top { display:flex; align-items:center; gap:12px; }
          .ring { width:44px; height:44px; border-radius:50%; background:#e8edff; display:grid; place-items:center; }
          .ring-inner { width:32px; height:32px; background:#fff; border-radius:50%; box-shadow: inset 0 0 0 3px #e8edff; }
          .pct { color:#2c388f; }
          .bar { margin-top:10px; background:#e8edff; border-radius:999px; height:10px; overflow:hidden; }
          .bar span { display:block; height:100%; background:#2c47ff; border-radius:999px; }

          @media (max-width: 960px) {
            .flow-grid { grid-template-columns: 1fr; }
            .notes-grid { grid-template-columns: 1fr; }
            .upload-row { grid-template-columns: 1fr; }
          }
        `}</style>
      </main>
    </div>
  );
}
